import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, exhaustMap, map, Observable, take, tap } from "rxjs";
import { LoaderService } from "./loader.service";
import { AuthenticationService } from "./authentication.service";
import { ActionResult, ActionResultList, Organiser } from "@model";
import { EnvironmentService } from "./environment.service";

@Injectable({ providedIn: 'root' })
export class OrganiserService {
  private static _organisers = new BehaviorSubject<ActionResultList<Organiser[], number> | undefined>(undefined);
  private static _currentOrganiser = new BehaviorSubject<Organiser | undefined>(undefined);
  private static _loggedInOrganiser = new BehaviorSubject<Organiser | undefined>(undefined);

  get organisers() {
    return OrganiserService._organisers;
  }

  get currentOrganiser() {
    return OrganiserService._currentOrganiser;
  }

  get loggedInOrganiser() {
    return OrganiserService._loggedInOrganiser;
  }

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private authService: AuthenticationService,
    private environmentService: EnvironmentService
  ) {}

  setCurrentOrganiser(organiser?: Organiser) {
    this.currentOrganiser.next(organiser);
  }

  getOrganiser(organiserId?: number) {
    this.http.post<ActionResult<Organiser>>(this.environmentService.getApiUrl() + "/api/organiser/getbyid", organiserId)
      .subscribe(actionResult => this.currentOrganiser.next(actionResult.result));
  }

  getLoggedInOrganiser() {
    this.http.get<ActionResult<Organiser>>(this.environmentService.getApiUrl() + "/api/organiser/details")
      .subscribe(actionResult => this.loggedInOrganiser.next(actionResult.result));
  }

  updateOrganiser(organiser: Organiser, onSuccess: (organiser: Organiser) => void, onError: () => void) {
    this.loaderService.startLoading();
    this.authService.jwtResponse.pipe(
      take(1),
      exhaustMap(() => this.http.post<ActionResult<Organiser>>(
        this.environmentService.getApiUrl() + (organiser.id ? "/api/organiser/update" : "/api/organiser/add"), organiser
      )),
      tap(() => this.loaderService.dismissLoading())
    ).subscribe(actionResult => {
      if (actionResult.resultCode !== -1 && actionResult.result) {
        onSuccess(actionResult.result);
      }
      else {
        onError();
      }
      this.currentOrganiser.next(actionResult.result);
    });
  }

  emptyOrganisers() {
    this.organisers.next(undefined);
    this.currentOrganiser.next(undefined);
    this.loggedInOrganiser.next(undefined);
  }

  deleteOrganiser(organiserId: number, onSuccess: () => void, onError: () => void) {
    this.authService.jwtResponse.pipe(
      take(1),
      exhaustMap(() => this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/organiser/remove", organiserId)),
      tap(() => this.loaderService.dismissLoading())
    ).subscribe(actionResult => actionResult.resultCode !== -1 ? onSuccess() : onError());
  }

  getOrganisers(): Observable<Organiser[]> {
    return this.http.get<ActionResultList<Organiser[], number>>(this.environmentService.getApiUrl() + "/api/organiser/getall").pipe(
      map(value => value?.result?.length ? value.result : [])
    );
  }

  getMeasurers(): Observable<Organiser[]> {
    return this.http.get<ActionResultList<Organiser[], number>>(this.environmentService.getApiUrl() + "/api/organiser/getmeasurers").pipe(
      map(value => value?.result?.length ? value.result : [])
    );
  }
}
